import { useReactiveVar } from '@apollo/client'
import { format } from 'date-fns'
import { toLower, without } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch } from 'react-router-dom'

import { DotSpan } from '@src/components/DotSpan/DotSpan'
import { FulfilmentOverlay } from '@src/components/FulfilmentOverlay/FulfilmentOverlay'
import { DeliveryLocationSection } from '@src/components/ServicePopover/DeliveryLocationSection'
import { SectionHeader } from '@src/components/ServicePopover/SectionHeader'
import { BottomSheet } from '@src/components/BottomSheet/BottomSheet'
import {
  DoneButton,
  MobileNavigationPopoverContent,
  MobileNavigationPopoverContentWrapper,
  MobileNavigationPopoverFooter,
  MobileNavigationPopoverFooterContent,
  MobileNavigationResetLink,
  SectionContentContainer,
  Separator,
} from '@src/components/BottomSheet/BottomSheet.styles'
import { WhenPopover } from '@src/components/ServicePopover/WhenPopover'
import { breakpoints } from '@src/constants/breakpoints'
import {
  LocationType,
  NarrowFulfilmentMethodInputType,
} from '@src/graphql-types'
import { useCategories } from '@src/hooks/useCategories/useCategories'
import { useFetchFulfilmentLabels } from '@src/hooks/useFetchFulfilmentLabels'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { FulfilmentFilterWhenType } from '@src/hooks/useFulfilmentFilter/validation'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { jwtVar } from '@src/models/customer/jwt'
import { screenResolutionVar } from '@src/models/screenResolution'
import { ALL_FULFILMENT_FILTER_WHEN_TYPES } from '@src/utils/fulfilment'

import {
  Container,
  ExpandButton,
  IconContainer,
  InnerContainer,
  StyledDoubleArrowSVG,
  OuterContainer,
  MobileSearchContainer,
} from './MobileNavigationStyles'

import { Search } from '../SearchBar/Search'
import { useBottomSheetStack } from '../../../hooks/useBottomSheetStack/useBottomSheetStack'

export const MobileNavigation: React.FC = () => {
  const jwt = useReactiveVar(jwtVar)
  const { serviceComponentBottomSheetController } = useBottomSheetStack()
  const [showSearchBar, setShowSearchBar] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { width } = useReactiveVar(screenResolutionVar)
  const fulfilmentFilter = useFulfilmentFilter({
    deferredUpdates: true,
  })
  const { t } = useTranslation('serviceNavigation')
  const marketplace = useMarketplace()
  const merchantURLPath = marketplace.urlPath
  const fulfilmentLabels = useFetchFulfilmentLabels()

  const isOutletListRoute = useMatch(
    `/:deliveryZone?/:merchantCategory(${merchantURLPath}`
  )

  const categories = useCategories()

  // on open, reset the fulfilment filter, as the deferred state may have been changed by something else (eg the invalid location modal)
  useEffect(() => {
    if (serviceComponentBottomSheetController.isOpen) {
      fulfilmentFilter.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceComponentBottomSheetController.isOpen])

  // categories page does not allow service selection
  // and so the button which opens the popover is only enabled
  // if there are no categories selected
  const enableServicePopover = !categories.selectedCategories.length

  useEffect(() => {
    if (!categories.selectedCategories.length) {
      setShowSearchBar(false)
    }
  }, [categories.selectedCategories.length])

  useEffect(() => {
    if (width >= breakpoints.phablet) {
      setShowSearchBar(false)
      setStartAnimation(false)
    }
  }, [width])

  const openServiceComponent = () => {
    fulfilmentFilter.setActiveFulfilmentMethods(
      fulfilmentFilter.savedData.sortedFulfilmentMethods
    )
    serviceComponentBottomSheetController.open()
  }

  const startClosingAnimation = () => {
    setStartAnimation(true)
    setShowSearchBar(false)
    setTimeout(() => {
      setStartAnimation(false)
    }, 1000)
  }

  const handleDone = () => {
    serviceComponentBottomSheetController.close()
    fulfilmentFilter.save()
  }

  const handleCancel = () => {
    serviceComponentBottomSheetController.close()
    fulfilmentFilter.reset()
  }

  const isNotTable = fulfilmentFilter.data.nonEmptyFulfilmentMethods.some(
    fulfilmentMethod =>
      [
        NarrowFulfilmentMethodInputType.DELIVERY,
        NarrowFulfilmentMethodInputType.COLLECTION,
      ].includes(fulfilmentMethod)
  )

  return (
    <>
      <OuterContainer>
        <Container
          showSearchBar={showSearchBar}
          startAnimation={startAnimation}
        >
          <InnerContainer
            onClick={() => {
              enableServicePopover && openServiceComponent()
            }}
            disabled={!enableServicePopover}
            isActive={serviceComponentBottomSheetController.isOpen}
          >
            <div>
              {fulfilmentLabels.every(({ fulfilment }) =>
                fulfilmentFilter.savedData.nonEmptyFulfilmentMethods.includes(
                  fulfilment
                )
              ) || !enableServicePopover
                ? marketplace.allOutletsText || t('all')
                : fulfilmentFilter.savedData.sortedFulfilmentMethods.map(
                    (fulfilment, index) => (
                      <span key={fulfilment}>
                        {index > 0 && <DotSpan />}
                        {t(toLower(fulfilment))}
                      </span>
                    )
                  )}
              {isOutletListRoute &&
                fulfilmentFilter.savedData.when.type ===
                  FulfilmentFilterWhenType.PREORDER &&
                isNotTable && (
                  <span>
                    {' '}
                    {format(
                      fulfilmentFilter.savedData.when.preorderDate,
                      `EEE dd MMM`
                    )}
                  </span>
                )}
            </div>
            <IconContainer
              onClick={() => {
                enableServicePopover && openServiceComponent()
              }}
              disabled={!enableServicePopover}
            >
              {enableServicePopover && (
                <StyledDoubleArrowSVG id="filterDoubleArrow" />
              )}
            </IconContainer>
          </InnerContainer>

          {width >= breakpoints.tabletMisc && <Search />}
        </Container>
        {/* mobile - expand search bar on click, show categories on expansion
          phablet to desktop - show search bar 50/50
      */}
        {width < breakpoints.tabletMisc && (
          <>
            <ExpandButton
              onClick={() => {
                if (!showSearchBar) {
                  setShowSearchBar(true)
                  // Focus the search input after the animation starts
                  setTimeout(() => {
                    searchInputRef.current?.focus()
                  }, 100)
                }
              }}
              $display={!showSearchBar}
            />
            <MobileSearchContainer>
              <Search
                showSearchBar={showSearchBar}
                startAnimation={startAnimation}
                startClosingAnimation={startClosingAnimation}
                inputRef={searchInputRef}
              />
            </MobileSearchContainer>
          </>
        )}
      </OuterContainer>
      {/* SERVICE Popover */}
      {enableServicePopover && (
        <BottomSheet
          bottomSheetController={serviceComponentBottomSheetController}
          maxWidth={584}
        >
          <MobileNavigationPopoverContentWrapper>
            <MobileNavigationPopoverContent>
              <SectionHeader
                header={t('service_header')}
                subheader={t('service_subheader')}
              />
              <SectionContentContainer>
                <FulfilmentOverlay
                  availableFulfilmentMethods={
                    marketplace.sortedAvailableFulfilmentMethods
                  }
                  selectedFulfilmentMethods={
                    fulfilmentFilter.data.sortedFulfilmentMethods
                  }
                  setSelectedFulfilmentMethods={
                    fulfilmentFilter.setActiveFulfilmentMethods
                  }
                />
              </SectionContentContainer>
              <Separator />
              {isNotTable && (
                <>
                  <SectionHeader
                    header={t('time_header')}
                    subheader={t('time_subheader')}
                  />
                  <SectionContentContainer>
                    <WhenPopover
                      availableWhenTypes={ALL_FULFILMENT_FILTER_WHEN_TYPES}
                      selectedWhen={fulfilmentFilter.data.when}
                      onDone={fulfilmentFilter.setWhen}
                    />
                  </SectionContentContainer>{' '}
                  <Separator />
                </>
              )}

              <SectionHeader
                header={t('location_header')}
                subheader={t('location_subheader')}
              />
              <SectionContentContainer>
                <DeliveryLocationSection
                  availableLocationTypes={
                    jwt
                      ? marketplace.availableLocationTypes
                      : without(
                          marketplace.availableLocationTypes,
                          LocationType.ADDRESS
                        )
                  }
                  selectedLocation={fulfilmentFilter.data.where.location}
                  setSelectedLocation={fulfilmentFilter.setLocationType}
                  historicalData={fulfilmentFilter.data.where.historicalData}
                  showSubtitles={false}
                  handleCancelWhenNavigatingAway={handleCancel}
                />
              </SectionContentContainer>
            </MobileNavigationPopoverContent>
          </MobileNavigationPopoverContentWrapper>
          <MobileNavigationPopoverFooter>
            <MobileNavigationPopoverFooterContent>
              <MobileNavigationResetLink
                // TODO: maybe disable if unchanged
                onClick={fulfilmentFilter.reset}
              >
                {t('reset')}
              </MobileNavigationResetLink>

              <DoneButton
                content={t('done')}
                width="180px"
                onClick={() => handleDone()}
                type={'button'}
              />
            </MobileNavigationPopoverFooterContent>
          </MobileNavigationPopoverFooter>
        </BottomSheet>
      )}
    </>
  )
}
